import React from 'react';
import { Info } from 'lucide-react';
import { Label } from '../../ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { AffectsScoreBadge } from './AffectsScoreBadge';
import { AffectsBadge } from './AffectsBadge';
import type { InputFieldId } from '../../../lib/inputResolution';

/**
 * IC3 — shared shape for the "inherit from Project Intent" mirror UI.
 *
 * @deprecated SSOT — the override mechanism for the 8 shared-layer
 * fields was deleted by `Site_SSOT_Prompt.md`. There are no production
 * callers post-SSOT (the fields the mirror used to surface — projectSizeMW,
 * inServiceDate, coolingPreference — no longer exist on the
 * *Preferences interfaces). This component is kept for two reasons:
 *   1. Library API stability (deprecation notice instead of immediate
 *      removal so any in-flight consumer surfaces a TS warning before
 *      a break).
 *   2. Future re-use if a per-report-only override surface is ever
 *      designed for a new field (e.g. a phase-specific MW override).
 *
 * Eligible for full removal in a SSOT-FOLLOWUP cleanup once we confirm
 * no in-flight branches still import it.
 *
 * The form renders a read-only row showing the inherited value with an
 * "Override for this report" toggle. When toggled on, the editable
 * input slot appears via the `editor` render prop.
 */
export interface InheritedMirrorFieldProps {
  label: string;
  fieldKey?: string;
  /** Resolved inherited value (e.g. `"300 MW"`, `"Apr 2028"`). */
  inheritedDisplay: string | null;
  /** Source label (e.g. `"Project Intent · exact"`). */
  inheritedFrom: string;
  /** True when the user has set a per-report override. */
  hasOverride: boolean;
  /** Toggle handler — `false` clears the override field. */
  onToggleOverride: (next: boolean) => void;
  /** Render prop for the editable input shown when override is on. */
  editor: () => React.ReactNode;
  help?: string;
  /**
   * IC5 — INPUT_PRECEDENCE field id to surface the "Affects: …" badge.
   * Optional — when absent the badge is suppressed (legacy behaviour).
   */
  inputFieldId?: InputFieldId;
}

/**
 * @deprecated SSOT — no production callers; orphan-cleanup pending.
 * See JSDoc on `InheritedMirrorFieldProps` for the rationale.
 *
 * IC3 — read-only "inherited from Project Intent" mirror with an
 * explicit "Override for this report" toggle.
 */
export const InheritedMirrorField: React.FC<InheritedMirrorFieldProps> = ({
  label,
  fieldKey,
  inheritedDisplay,
  inheritedFrom,
  hasOverride,
  onToggleOverride,
  editor,
  help,
  inputFieldId,
}) => {
  return (
    <div
      className="space-y-1.5"
      data-testid={fieldKey ? `pref-field-${fieldKey}` : undefined}
      data-inherit={hasOverride ? 'overridden' : 'inherited'}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <Label className="type-section text-text-secondary">
          {label}
        </Label>
        {help && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" tabIndex={-1} className="text-text-muted hover:text-text-primary">
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] text-xs">{help}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <span className="inline-flex items-center type-meta uppercase tracking-widest text-text-secondary border border-border bg-surface/30 px-1 py-0.5">
          {hasOverride ? 'Overridden' : 'Inherited'}
        </span>
        {inputFieldId && <AffectsBadge fieldId={inputFieldId} testKey={fieldKey} />}
      </div>
      {!hasOverride && (
        <div className="text-xs text-text-primary">
          {inheritedDisplay ?? <span className="text-text-muted italic">(not set in Project Intent)</span>}
          <span className="ml-2 type-body uppercase tracking-widest text-text-muted">
            from {inheritedFrom}
          </span>
        </div>
      )}
      {hasOverride && (
        <div className="space-y-1">{editor()}</div>
      )}
      <button
        type="button"
        onClick={() => onToggleOverride(!hasOverride)}
        className="type-body uppercase tracking-widest text-accent hover:underline"
        data-testid={fieldKey ? `pref-override-toggle-${fieldKey}` : undefined}
      >
        {hasOverride ? 'Revert to inherited' : 'Override for this report'}
      </button>
      {/* IC6 — surface inter-layer disagreement when the override
          value disagrees with the inherited (lower-precedence) value. */}
      {hasOverride && inheritedDisplay && (
        <InterLayerDisagreementNotice
          inheritedDisplay={inheritedDisplay}
          inheritedFrom={inheritedFrom}
          fieldKey={fieldKey}
        />
      )}
    </div>
  );
};

/**
 * IC6 — non-blocking inline warning shown when a typed-prefs override
 * disagrees with the inherited Project Intent value. The user can
 * "Adopt Project Intent value" (clear the override) or "Keep override"
 * (dismiss the notice). Mirrors the existing crossFieldWarnings + SA7
 * diff-tab patterns.
 */
const InterLayerDisagreementNotice: React.FC<{
  inheritedDisplay: string;
  inheritedFrom: string;
  fieldKey?: string;
}> = ({ inheritedDisplay, inheritedFrom, fieldKey }) => (
  <div
    className="mt-1 type-body text-warning border-l-2 border-warning/40 pl-2"
    data-testid={fieldKey ? `pref-disagreement-${fieldKey}` : undefined}
  >
    Overriding inherited value ({inheritedDisplay} from {inheritedFrom}) for this report only.
  </div>
);

// Defined with the pure validators in lib; re-exported for the form modules.
export type { ValidationResult } from '../../../lib/reportPreferences/validation';

/**
 * Configuration Upgrade (CU2) — context that lets per-type FieldRows
 * read the list of missing-required field KEYS from validators without
 * threading props through every form component. The parent
 * `ReportTypePreferencesFields` wraps the form in a provider; each
 * FieldRow opts-in via its `fieldKey` prop.
 *
 * Also carries the per-field score-affecting + score-warning hints
 * surfaced by CU3 + CU4. Components read whichever subset of fields
 * they need.
 */
export interface PrefsValidationCtx {
  /** Field keys that are required-but-missing. */
  missingRequiredKeys: string[];
  /** Field keys that fall in the score-affecting band (CU4). */
  scoreAffectingKeys: string[];
  /** Cross-field warnings keyed by field name (CU3). Empty when none. */
  warningsByField: Record<string, { severity: 'warn' | 'error'; message: string }>;
}

const PrefsValidationContext = React.createContext<PrefsValidationCtx>({
  missingRequiredKeys: [],
  scoreAffectingKeys: [],
  warningsByField: {},
});

export const PrefsValidationProvider: React.FC<{
  value: PrefsValidationCtx;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <PrefsValidationContext.Provider value={value}>
    {children}
  </PrefsValidationContext.Provider>
);

export function usePrefsValidation(): PrefsValidationCtx {
  return React.useContext(PrefsValidationContext);
}

/**
 * Shared field row for all per-type preference sub-forms. Renders a label,
 * an optional required marker, an optional tooltip help icon, and the input.
 *
 * CU2: when `fieldKey` is set and the validation context lists the key
 * as missing, the row renders with a red left border, an inline
 * "Required" badge, and `aria-invalid="true"` on its child input wrapper.
 *
 * CU4: when `fieldKey` matches the validation context's
 * scoreAffectingKeys, an "Affects score" chip renders next to the label.
 */
export const FieldRow: React.FC<{
  label: string;
  help?: string;
  required?: boolean;
  /** Per-type field key (e.g. 'projectSizeMW'). Enables CU2 + CU4 + CU3 wiring. */
  fieldKey?: string;
  children: React.ReactNode;
}> = ({ label, help, required, fieldKey, children }) => {
  const ctx = usePrefsValidation();
  const isMissing = !!(fieldKey && ctx.missingRequiredKeys.includes(fieldKey));
  const isScoreAffecting = !!(fieldKey && ctx.scoreAffectingKeys.includes(fieldKey));
  const warning = fieldKey ? ctx.warningsByField[fieldKey] : undefined;
  const errorId = fieldKey ? `pref-err-${fieldKey}` : undefined;

  return (
    <div
      className={`space-y-1.5${isMissing ? ' border-l-2 border-danger pl-2' : ''}`}
      data-testid={fieldKey ? `pref-field-${fieldKey}` : undefined}
      data-invalid={isMissing ? 'true' : undefined}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <Label className="type-section text-text-secondary">
          {label}{required && <span className="text-danger ml-0.5">*</span>}
        </Label>
        {help && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" tabIndex={-1} className="text-text-muted hover:text-text-primary">
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] text-xs">{help}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isScoreAffecting && fieldKey && <AffectsScoreBadge fieldKey={fieldKey} />}
        {isMissing && (
          <span
            className="inline-flex items-center type-meta uppercase tracking-widest text-danger rounded-md border border-danger/40 bg-danger/5 px-1 py-0.5"
            data-testid={`required-badge-${fieldKey}`}
          >
            Required
          </span>
        )}
      </div>
      <div aria-invalid={isMissing || undefined} aria-describedby={isMissing ? errorId : undefined}>
        {children}
      </div>
      {isMissing && (
        <div id={errorId} className="type-body text-danger" data-testid={`pref-err-${fieldKey}`}>
          Required for this report type
        </div>
      )}
      {warning && (
        <div
          className={`type-body ${warning.severity === 'error' ? 'text-danger' : 'text-warning'}`}
          data-testid={`pref-warning-${fieldKey}`}
        >
          {warning.message}
        </div>
      )}
    </div>
  );
};
